import { useCallback, type Dispatch, type MutableRefObject } from "react";
import type { CmmdTaskComment } from "@cmmd-forge/contracts";

import { createCmmdApiClient } from "~/environments/cmmdApi";
import type { ProjectTaskWorkbenchAction } from "./useProjectTaskWorkbenchData";

const api = createCmmdApiClient();

export interface TaskCommentMutationInput {
  readonly taskId: number | null;
  readonly commentsRef: MutableRefObject<readonly CmmdTaskComment[]>;
  readonly activeTaskIdRef: MutableRefObject<number | null>;
  readonly editIdempotencyKeysRef: MutableRefObject<Map<string, string>>;
  readonly deleteIdempotencyKeysRef: MutableRefObject<Map<string, string>>;
  readonly dispatch: Dispatch<ProjectTaskWorkbenchAction>;
  readonly errorMessage: (cause: unknown, fallback: string) => string;
}

export function useTaskCommentMutations(input: TaskCommentMutationInput) {
  const createComment = useCallback(
    (content: string) => createTaskComment(input, content),
    [input],
  );
  const updateComment = useCallback(
    (commentKey: string, content: string) => updateTaskComment(input, commentKey, content),
    [input],
  );
  const deleteComment = useCallback(
    (commentKey: string) => deleteTaskComment(input, commentKey),
    [input],
  );
  return { createComment, updateComment, deleteComment } as const;
}

async function createTaskComment(input: TaskCommentMutationInput, content: string): Promise<void> {
  if (input.taskId === null) return;
  input.dispatch({ type: "comment-start" });
  try {
    const comment = await api.createProjectTaskComment(input.taskId, { content });
    if (input.activeTaskIdRef.current === input.taskId) {
      input.dispatch({ type: "comment-ok", comment });
    }
  } catch (cause) {
    input.dispatch({
      type: "comment-error",
      message: input.errorMessage(cause, "Could not add comment."),
    });
    throw cause;
  }
}

async function updateTaskComment(
  input: TaskCommentMutationInput,
  commentKey: string,
  content: string,
): Promise<void> {
  if (input.taskId === null) return;
  const previousComments = input.commentsRef.current;
  const current = previousComments.find((comment) => comment.commentKey === commentKey);
  const operationKey = `${input.taskId}:${commentKey}:${content}`;
  const idempotencyKey =
    input.editIdempotencyKeysRef.current.get(operationKey) ?? crypto.randomUUID();
  input.editIdempotencyKeysRef.current.set(operationKey, idempotencyKey);
  input.dispatch({ type: "comment-start" });
  if (current) {
    input.dispatch({
      type: "comment-optimistic-replaced",
      comment: { ...current, content, updatedAt: new Date().toISOString() },
    });
  }
  try {
    const comment = await api.updateProjectTaskComment(input.taskId, commentKey, {
      content,
      idempotencyKey,
    });
    input.editIdempotencyKeysRef.current.delete(operationKey);
    if (input.activeTaskIdRef.current === input.taskId) {
      input.dispatch({ type: "comment-replaced", comment });
    }
  } catch (cause) {
    restoreCommentAfterFailure(
      input,
      previousComments,
      input.errorMessage(cause, "Could not update comment."),
    );
    throw cause;
  }
}

async function deleteTaskComment(
  input: TaskCommentMutationInput,
  commentKey: string,
): Promise<void> {
  if (input.taskId === null) return;
  const previousComments = input.commentsRef.current;
  const operationKey = `${input.taskId}:${commentKey}`;
  const idempotencyKey =
    input.deleteIdempotencyKeysRef.current.get(operationKey) ?? crypto.randomUUID();
  input.deleteIdempotencyKeysRef.current.set(operationKey, idempotencyKey);
  input.dispatch({ type: "comment-start" });
  input.dispatch({ type: "comment-optimistic-removed", commentKey });
  try {
    const receipt = await api.deleteProjectTaskComment(input.taskId, commentKey, {
      idempotencyKey,
    });
    input.deleteIdempotencyKeysRef.current.delete(operationKey);
    if (input.activeTaskIdRef.current === input.taskId) {
      input.dispatch({ type: "comment-removed", commentKey: receipt.deletedCommentKey });
    }
  } catch (cause) {
    restoreCommentAfterFailure(
      input,
      previousComments,
      input.errorMessage(cause, "Could not delete comment."),
    );
    throw cause;
  }
}

function restoreCommentAfterFailure(
  input: TaskCommentMutationInput,
  comments: readonly CmmdTaskComment[],
  message: string,
): void {
  if (input.activeTaskIdRef.current !== input.taskId) return;
  input.dispatch({ type: "comment-restored", comments });
  input.dispatch({ type: "comment-error", message });
}
